// Réglages › Dossiers › Juridictions : la liste partagée du cabinet.
// On y renomme, on change le type, on désactive, on fusionne deux doublons.
import { useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { FaCheck, FaPen, FaPlus, FaTrashCan, FaXmark } from '../ui/FaIcons';
import {
  useCreateJurisdiction,
  useDeleteJurisdiction,
  useJurisdictionsWithUsage,
  useMergeJurisdictions,
  useUpdateJurisdiction,
} from '../../hooks/queries/useJurisdictions';
import {
  guessJurisdictionKind,
  JURISDICTION_KINDS,
  JURISDICTION_KIND_LABELS,
  type JurisdictionKind,
  type JurisdictionWithUsage,
} from '../../services/dossiers/jurisdictionService';
import { dossiersByJurisdictionHref } from '../../lib/dossiers/dossierListLinks';

const selectClass =
  'h-9 rounded-md border border-neutral-200 bg-white px-2 text-sm outline-none focus:border-cime-primary';

function fail(err: unknown) {
  toast.error(err instanceof Error ? err.message : 'Action impossible');
}

export function JurisdictionsSection() {
  const { data: jurisdictions = [], isLoading } = useJurisdictionsWithUsage();
  const create = useCreateJurisdiction();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<JurisdictionKind | null>(null);
  const newKind = kind ?? guessJurisdictionKind(name);

  function add(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate(
      { name, kind: newKind },
      {
        onSuccess: () => {
          setName('');
          setKind(null);
        },
        onError: fail,
      },
    );
  }

  return (
    <section className="rounded-cime border border-neutral-200 bg-white p-4 space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-cime-text-primary">Juridictions</h2>
        <p className="text-sm text-cime-text-muted mt-1">
          La liste proposée dans les dossiers. Chacun peut y ajouter une juridiction en créant un dossier ;
          ici, vous renommez, vous fusionnez les doublons et vous désactivez celles qui ne servent plus.
        </p>
      </div>

      <form onSubmit={add} className="flex flex-wrap items-center gap-2">
        <div className="min-w-[240px] flex-1">
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setKind(null);
            }}
            placeholder="Nouvelle juridiction : TJ Caen, CA Caen…"
            className="h-9"
          />
        </div>
        <select value={newKind} onChange={(e) => setKind(e.target.value as JurisdictionKind)} aria-label="Type" className={selectClass}>
          {JURISDICTION_KINDS.map((k) => (
            <option key={k} value={k}>{JURISDICTION_KIND_LABELS[k]}</option>
          ))}
        </select>
        <Button type="submit" disabled={!name.trim() || create.isPending}>
          <FaPlus className="mr-1.5 h-3 w-3" aria-hidden />
          Ajouter
        </Button>
      </form>

      {isLoading ? (
        <p className="text-sm text-cime-text-muted">Chargement…</p>
      ) : jurisdictions.length === 0 ? (
        <p className="text-sm text-cime-text-muted">Aucune juridiction pour l’instant.</p>
      ) : (
        <ul className="m-0 list-none divide-y divide-neutral-100 rounded-md border border-neutral-200 p-0">
          {jurisdictions.map((j) => (
            <JurisdictionRow key={j.id} jurisdiction={j} others={jurisdictions.filter((o) => o.id !== j.id)} />
          ))}
        </ul>
      )}
    </section>
  );
}

function JurisdictionRow({ jurisdiction, others }: { jurisdiction: JurisdictionWithUsage; others: JurisdictionWithUsage[] }) {
  const update = useUpdateJurisdiction();
  const remove = useDeleteJurisdiction();
  const merge = useMergeJurisdictions();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(jurisdiction.name);
  const [merging, setMerging] = useState(false);
  const [targetId, setTargetId] = useState('');
  const count = jurisdiction.dossierCount;
  const target = others.find((o) => o.id === targetId);

  function rename() {
    const next = name.trim();
    if (!next || next === jurisdiction.name) {
      setEditing(false);
      setName(jurisdiction.name);
      return;
    }
    update.mutate({ id: jurisdiction.id, patch: { name: next } }, { onSuccess: () => setEditing(false), onError: fail });
  }

  function confirmMerge() {
    if (!target) return;
    merge.mutate(
      { sourceId: jurisdiction.id, targetId: target.id },
      {
        onSuccess: (moved) =>
          toast.success(`« ${jurisdiction.name} » fusionnée dans « ${target.name} » — ${moved} dossier${moved > 1 ? 's' : ''} déplacé${moved > 1 ? 's' : ''}`),
        onError: fail,
      },
    );
  }

  return (
    <li className={cn('flex flex-col gap-2 px-3 py-2.5', !jurisdiction.is_active && 'bg-neutral-50')}>
      <div className="flex flex-wrap items-center gap-2">
        {editing ? (
          <form
            className="flex min-w-[200px] flex-1 items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              rename();
            }}
          >
            <div className="min-w-0 flex-1">
              <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
            </div>
            <IconButton label="Enregistrer le nom" type="submit"><FaCheck className="h-3.5 w-3.5" /></IconButton>
            <IconButton label="Annuler" onClick={() => { setEditing(false); setName(jurisdiction.name); }}><FaXmark className="h-3.5 w-3.5" /></IconButton>
          </form>
        ) : (
          <span className={cn('min-w-[200px] flex-1 truncate text-sm font-medium', jurisdiction.is_active ? 'text-cime-text-primary' : 'text-neutral-400')}>
            {jurisdiction.name}
            {!jurisdiction.is_active ? <span className="ml-2 text-[11px] font-normal">désactivée</span> : null}
          </span>
        )}
        <select
          value={jurisdiction.kind}
          onChange={(e) => update.mutate({ id: jurisdiction.id, patch: { kind: e.target.value as JurisdictionKind } }, { onError: fail })}
          aria-label={`Type de ${jurisdiction.name}`}
          className={cn(selectClass, 'w-[210px]')}
        >
          {JURISDICTION_KINDS.map((k) => (
            <option key={k} value={k}>{JURISDICTION_KIND_LABELS[k]}</option>
          ))}
        </select>
        {count > 0 ? (
          <Link to={dossiersByJurisdictionHref(jurisdiction.name)} className="w-[92px] text-right text-[13px] tabular-nums text-cime-primary underline decoration-cime-primary/30 underline-offset-2 hover:decoration-cime-primary">
            {count} dossier{count > 1 ? 's' : ''}
          </Link>
        ) : (
          <span className="w-[92px] text-right text-[13px] text-neutral-400">0 dossier</span>
        )}
        <div className="flex items-center gap-1">
          {!editing ? <IconButton label="Renommer" onClick={() => setEditing(true)}><FaPen className="h-3.5 w-3.5" /></IconButton> : null}
          <button
            type="button"
            onClick={() => setMerging((v) => !v)}
            disabled={others.length === 0}
            className="h-8 rounded-md px-2 text-[13px] font-medium text-cime-text-muted transition-colors hover:bg-neutral-100 hover:text-cime-primary disabled:opacity-40"
          >
            Fusionner
          </button>
          <button
            type="button"
            onClick={() => update.mutate({ id: jurisdiction.id, patch: { is_active: !jurisdiction.is_active } }, { onError: fail })}
            className="h-8 rounded-md px-2 text-[13px] font-medium text-cime-text-muted transition-colors hover:bg-neutral-100 hover:text-cime-primary"
          >
            {jurisdiction.is_active ? 'Désactiver' : 'Réactiver'}
          </button>
          <IconButton
            label={count > 0 ? 'Suppression impossible : des dossiers l’utilisent. Fusionnez-la ou désactivez-la.' : 'Supprimer'}
            disabled={count > 0 || remove.isPending}
            danger
            onClick={() => remove.mutate(jurisdiction.id, { onError: fail })}
          >
            <FaTrashCan className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      </div>
      {merging ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-[#fbfaf7] px-3 py-2 text-[13px] text-cime-text-muted">
          <span>
            Passer {count > 0 ? `les ${count} dossier${count > 1 ? 's' : ''} de` : ''} « {jurisdiction.name} » dans :
          </span>
          <select value={targetId} onChange={(e) => setTargetId(e.target.value)} aria-label="Juridiction à garder" className={cn(selectClass, 'min-w-[200px]')}>
            <option value="">Choisir la juridiction à garder…</option>
            {others.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <Button type="button" size="sm" disabled={!target || merge.isPending} onClick={confirmMerge}>
            Fusionner
          </Button>
          <span className="text-xs">« {jurisdiction.name} » disparaîtra de la liste.</span>
        </div>
      ) : null}
    </li>
  );
}

function IconButton({
  label, children, onClick, type = 'button', disabled, danger,
}: {
  label: string;
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={cn(
        'grid h-8 w-8 place-items-center rounded-md text-neutral-400 transition-colors disabled:cursor-not-allowed disabled:opacity-40',
        danger ? 'hover:bg-red-50 hover:text-red-600 disabled:hover:bg-transparent disabled:hover:text-neutral-400' : 'hover:bg-neutral-100 hover:text-cime-primary',
      )}
    >
      {children}
    </button>
  );
}
